import type { ZodType } from "zod";
import z from "zod";

import { isInvolvementKindHeldByOneAtMost } from "../rules/isInvolvementKindHeldByOneAtMost.js";
import { isInvolvementKindStartable } from "../rules/isInvolvementKindStartable.js";
import {
  PersonInvolvementKindSchema,
  type PersonInvolvementKind,
} from "./involvement.js";
import {
  OrganisationIdSchema,
  type OrganisationIdUnbranded,
} from "./organisation.js";
import { UserIdSchema, type UserIdUnbranded } from "./user.js";

/**
 * One party being added to a role: a body, or a person and the body they are
 * involved on behalf of.
 *
 * Never a guest: a guest is to be named with the date their involvement ends,
 * and an addition states none.
 */
export type InvolvementAdditionUnbranded =
  | { kind: "organisation"; organisationId: OrganisationIdUnbranded }
  | {
      kind: "user";
      userId: UserIdUnbranded;
      onBehalfOfOrganisationId: OrganisationIdUnbranded;
    };

export const InvolvementAdditionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("organisation"),
    organisationId: OrganisationIdSchema,
  }),
  z.object({
    kind: z.literal("user"),
    userId: UserIdSchema,
    onBehalfOfOrganisationId: OrganisationIdSchema,
  }),
]) satisfies ZodType<InvolvementAdditionUnbranded>;

/**
 * Who a party is, whatever body they are involved on behalf of.
 *
 * A person is one party wherever they work: they hold a kind of involvement
 * once, whichever body they hold it through.
 */
export const partyKey = (
  party:
    | { kind: "organisation"; organisationId: string }
    | { kind: "user"; userId: string },
): string =>
  party.kind === "organisation"
    ? `organisation|${party.organisationId}`
    : `user|${party.userId}`;

export type StartInvolvementsRequestUnbranded = {
  kind: PersonInvolvementKind;
  parties: InvolvementAdditionUnbranded[];
};

export const StartInvolvementsRequestSchema = z
  .object({
    kind: PersonInvolvementKindSchema.refine(isInvolvementKindStartable, {
      message: "involvement.addition.notAddable",
    }),
    parties: z
      .array(InvolvementAdditionSchema)
      .min(1, "involvement.addition.nothingChosen"),
  })
  .refine(
    (request) =>
      !isInvolvementKindHeldByOneAtMost(request.kind) ||
      request.parties.length <= 1,
    {
      message: "involvement.addition.oneHolder",
      path: ["parties"],
    },
  )
  .refine(
    (request) =>
      new Set(request.parties.map(partyKey)).size === request.parties.length,
    { message: "involvement.addition.duplicate", path: ["parties"] },
  )
  .brand<"haven-common:StartInvolvementsRequest">() satisfies ZodType<StartInvolvementsRequestUnbranded>;

/**
 * Starting involvements for parties in one role at once, as the person running
 * the case confirms them together.
 *
 * One role per request, because choosing the role is how the act starts.
 * Nobody states when it starts or ends: each involvement starts on the day it
 * is confirmed. Nor does a request say whose work an approver approves — that is
 * the `liveDesignatedAssessorInvolvement` rule, so the two cannot disagree.
 */
export type StartInvolvementsRequest = z.infer<
  typeof StartInvolvementsRequestSchema
>;
